import { useEffect, useMemo, useState } from "react";
import { getSalesmanScorecard } from "../../distflowAPI/salesAnalyticsApi";
import "./SalesmanScorecard.css";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
const money = (value) => value == null ? "—" : currency.format(Number(value));
const pct = (value) => value == null ? "—" : `${Math.round(Number(value))}%`;
const signedPct = (value) => {
  if (value == null) return null;
  const number = Number(value);
  return `${number > 0 ? "+" : number < 0 ? "−" : ""}${Math.abs(number).toFixed(1)}%`;
};
const tone = (value) => value == null ? "flat" : Number(value) > 0 ? "gain" : Number(value) < 0 ? "decline" : "flat";

export const FLAG_LABELS = {
  low_coverage: "Few accounts ordering",
  losing_customers: "Losing customers",
  shrinking_accounts: "Accounts shrinking",
  unattended_risk: "At-risk customers without follow-up",
  no_new_business: "No new business",
  no_tasks: "No tasks logged",
};
const FLAG_HELP = {
  low_coverage: "Share of their accounts that ordered is 15+ points below the team median.",
  losing_customers: "Kept 10+ points fewer of last period’s customers than the team median.",
  shrinking_accounts: "Customers they kept are spending 10%+ less.",
  unattended_risk: "3 or more customers need attention and most have no open task.",
  no_new_business: "No new accounts and no first-time customers in these dates.",
  no_tasks: "Logged no account tasks while others on the team did.",
};

const COLUMNS = [
  { key: "sales", label: "Sales", value: (row) => Number(row.sales) },
  { key: "coverage_percent", label: "Accounts ordering", title: "Share of their active accounts that ordered in these dates", value: (row) => Number(row.coverage_percent ?? -1) },
  { key: "sales_per_account_weekly", label: "$ / account / wk", value: (row) => Number(row.sales_per_account_weekly ?? -1) },
  { key: "retention_percent", label: "Kept customers", title: "Customers in their book who ordered in the comparison dates and ordered again", value: (row) => Number(row.retention_percent ?? -1), comparison: true },
  { key: "existing_customer_growth_percent", label: "Existing customers", title: "Sales change from customers who bought in both periods", value: (row) => Number(row.existing_customer_growth_percent ?? -999), comparison: true },
  { key: "new_customers", label: "New business", title: "First-time customers in their book, and accounts opened in these dates", value: (row) => row.new_customers + row.new_accounts },
  { key: "categories_per_customer", label: "Categories", title: "Average product categories bought per active customer in their book", value: (row) => Number(row.categories_per_customer ?? -1) },
  { key: "at_risk_weekly", label: "At risk", value: (row) => Number(row.at_risk_weekly) },
  { key: "tasks_logged", label: "Tasks", value: (row) => row.tasks_logged ?? -1 },
  { key: "standing_order_share_percent", label: "Standing orders", title: "Share of their sales from accounts with an active standing order", value: (row) => Number(row.standing_order_share_percent ?? -1) },
];

function Median({ value, format }) {
  return value == null ? null : <small className="salesmen-median">median {format(value)}</small>;
}

export default function SalesmanScorecard({ request, onSelectSalesman }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "sales", direction: "desc" });
  const signature = JSON.stringify({
    start_date: request?.start_date, end_date: request?.end_date, territory_id: request?.territory_id,
    comparison: request?.comparison, comparison_start_date: request?.comparison_start_date, comparison_end_date: request?.comparison_end_date,
  });

  useEffect(() => {
    const params = JSON.parse(signature);
    if (!params.start_date) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    Promise.resolve()
      .then(() => getSalesmanScorecard(params, { signal: controller.signal }))
      .then((result) => { if (!controller.signal.aborted) setData(result || null); })
      .catch((requestError) => {
        if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
        setData(null);
        setError("The salesman scorecard could not be loaded.");
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [signature]);

  const hasComparison = Boolean(data?.comparison);
  const columns = COLUMNS.filter((column) => !column.comparison || hasComparison);
  const rows = useMemo(() => {
    const column = COLUMNS.find((item) => item.key === sort.key) || COLUMNS[0];
    const list = [...(data?.rows || [])];
    list.sort((a, b) => {
      if (a.is_office !== b.is_office) return a.is_office ? 1 : -1;
      const difference = column.value(a) - column.value(b);
      return (sort.direction === "asc" ? difference : -difference) || a.name.localeCompare(b.name);
    });
    return list;
  }, [data, sort]);

  if (!data && !loading && !error) return null;
  const medians = data?.team?.medians || {};
  const tasksInUse = data?.team?.tasks_in_use;
  const toggleSort = (key) => setSort((current) => current.key === key
    ? { key, direction: current.direction === "desc" ? "asc" : "desc" } : { key, direction: "desc" });

  return <section className="salesmen" aria-label="Salesmen" aria-busy={loading}>
    <div className="sales-analytics-section-head">
      <div><span className="sales-analytics-eyebrow">How the team is doing</span><h2>Salesmen</h2></div>
      <span className="sales-analytics-section-aside">Sales follow each order’s salesman. Everything else is measured on each rep’s book: the active accounts assigned to them.</span>
    </div>
    {error && <p className="salesmen-error" role="alert">{error}</p>}
    {loading && !data && <p className="salesmen-empty">Scoring the team…</p>}
    {data && <div className={`salesmen-card${loading ? " salesmen-card--refreshing" : ""}`}>
      <p className="salesmen-caveat">This shows results and follow-through. Visits, calls and who entered each order aren’t recorded yet, so effort itself can’t be measured.{!tasksInUse && " No account tasks were logged by salesmen in these dates, so the Tasks column is empty."}</p>
      {rows.length ? <div className="salesmen-table-wrap"><table aria-label="Salesman scorecard">
        <thead><tr>
          <th scope="col">Salesman</th>
          {columns.map((column) => <th scope="col" key={column.key} aria-sort={sort.key === column.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
            <button type="button" className="salesmen-sort" title={column.title} onClick={() => toggleSort(column.key)}>{column.label}<span aria-hidden="true">{sort.key === column.key ? (sort.direction === "asc" ? " ↑" : " ↓") : " ↕"}</span></button>
          </th>)}
        </tr></thead>
        <tbody>{rows.map((row) => <tr key={row.key} className={row.is_office ? "is-office" : ""}>
          <th scope="row">
            <button type="button" className="salesmen-name" title={`Focus the page on ${row.name}`} onClick={() => onSelectSalesman?.(row)}>{row.name}</button>
            {row.flags?.length > 0 && <span className="salesmen-flags">{row.flags.map((flag) => <span key={flag} className="salesmen-flag" title={FLAG_HELP[flag]}>{FLAG_LABELS[flag] || flag}</span>)}</span>}
          </th>
          <td>{money(row.sales)}
            {hasComparison ? <small className={`salesmen-${tone(row.sales_change_percent)}`}>{signedPct(row.sales_change_percent) ?? "no baseline"}</small> : <small>{row.orders} orders</small>}</td>
          <td>{row.book_accounts ? <>{pct(row.coverage_percent)}<small>{row.active_book_accounts} of {row.book_accounts}</small></> : "—"}
            {!row.is_office && <Median value={medians.coverage_percent} format={pct} />}</td>
          <td>{money(row.sales_per_account_weekly)}{!row.is_office && <Median value={medians.sales_per_account_weekly} format={money} />}</td>
          {hasComparison && <td>{row.previous_book_customers ? <>{pct(row.retention_percent)}<small>{row.retained_customers} of {row.previous_book_customers}{row.lost_customers ? ` · lost ${row.lost_customers} (${money(row.lost_customer_sales)})` : ""}</small></> : "—"}
            {!row.is_office && <Median value={medians.retention_percent} format={pct} />}</td>}
          {hasComparison && <td><span className={`salesmen-${tone(row.existing_customer_growth_percent)}`}>{signedPct(row.existing_customer_growth_percent) ?? "—"}</span></td>}
          <td>{row.new_customers} {row.new_customers === 1 ? "customer" : "customers"}<small>{row.new_accounts} {row.new_accounts === 1 ? "account" : "accounts"} opened</small></td>
          <td>{row.categories_per_customer ?? "—"}{!row.is_office && <Median value={medians.categories_per_customer} format={(value) => Number(value).toFixed(1)} />}</td>
          <td>{row.at_risk_customers ? <>{row.at_risk_customers} {row.at_risk_customers === 1 ? "customer" : "customers"}<small>{money(row.at_risk_weekly)}/wk · {row.at_risk_without_follow_up} no task</small></> : <span className="salesmen-muted">None</span>}</td>
          <td>{row.tasks_logged == null ? <span className="salesmen-muted">—</span> : <>{row.tasks_logged} logged<small>{row.tasks_completed} done{row.tasks_overdue ? ` · ${row.tasks_overdue} overdue` : ""}</small></>}</td>
          <td>{pct(row.standing_order_share_percent)}</td>
        </tr>)}</tbody>
      </table></div> : <p className="salesmen-empty">No salesman activity in these dates.</p>}
    </div>}
  </section>;
}
